import { PerformanceMonitor } from "@react-three/drei";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { XR, createXRStore } from "@react-three/xr";
import { Suspense, useEffect, useRef, type ReactNode, type RefObject } from "react";
import * as THREE from "three";
import { DEFAULT_PLACEMENT, WEBXR_FIXED_PLACEMENT, type PlacementPose } from "@/runtime/types";
import { isImmersiveMode, qualityPolicy, renderQualityForMode } from "@/scene/qualityPolicy";
import type { SceneStageProps } from "@/scene/types";
import { usePlacementGestures } from "@/scene/usePlacementGestures";
import { WebXRPlacement } from "@/scene/WebXRPlacement";
import { attachWebglLifecycle } from "@/scene/webglLifecycle";

const overlayRoot = document.getElementById("root") ?? document.body;

export const xrStore = createXRStore({
  offerSession: false,
  enterGrantedSession: false,
  emulate: false,
  controller: false,
  hand: false,
  gaze: false,
  transientPointer: false,
  screenInput: true,
  domOverlay: overlayRoot,
  hitTest: true,
  planeDetection: false,
  meshDetection: false,
  depthSensing: false,
  frameBufferScaling: "mid",
});

export function SceneStage(props: SceneStageProps) {
  const immersive = isImmersiveMode(props.mode);
  const policy = qualityPolicy(props.quality, immersive);
  const renderQuality = renderQualityForMode(props.quality, props.mode);
  return (
    <Canvas
      className="experience-canvas"
      camera={{ position: [0, 0.55, 4.7], fov: 42, near: 0.01, far: 100 }}
      dpr={policy.dpr}
      gl={{ alpha: true, antialias: policy.antialias, powerPreference: immersive ? "default" : "high-performance" }}
      shadows={policy.shadows}
      onCreated={({ gl }) => {
        gl.outputColorSpace = THREE.SRGBColorSpace;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 1.1;
        gl.domElement.dataset.testid = "experience-canvas";
      }}
    >
      <XR store={xrStore}>
        {props.onWebglContextLost ? <WebglLifecycle onLost={props.onWebglContextLost} /> : null}
        <SceneContents {...props} quality={renderQuality} allowWebXRPlacement />
      </XR>
    </Canvas>
  );
}

export function SceneContents(props: SceneStageProps & { allowWebXRPlacement?: boolean }) {
  const policy = qualityPolicy(props.quality, isImmersiveMode(props.mode));
  const placementGesturesEnabled = useRef(true);
  const setPlacementGesturesEnabled = (enabled: boolean) => { placementGesturesEnabled.current = enabled; };
  const placementPose = props.placement
    ?? (props.mode === "webxr" ? WEBXR_FIXED_PLACEMENT : props.mode === "eighth-wall" ? DEFAULT_PLACEMENT : null);
  return (
    <>
      {props.mode === "eighth-wall" ? <ExternalFrameOwner /> : null}
      {props.mode === "non-ar" ? <color attach="background" args={["#030712"]} /> : null}
      <fog attach="fog" args={["#050917", 6, 14]} />
      <ambientLight intensity={0.9} color="#819cff" />
      <directionalLight position={[3, 5, 2]} intensity={2.8} color="#d7f7ff" castShadow={policy.shadows !== false} />
      <pointLight position={[-3, 1, 2]} intensity={5} color="#2eeeff" distance={9} />
      <pointLight position={[3, -1, 1]} intensity={4} color="#9c5cff" distance={8} />
      {props.allowWebXRPlacement && props.mode === "webxr" && props.placement === null ? (
        <WebXRPlacement onPlacement={props.onPlacement} onProgress={props.onPlacementProgress} />
      ) : null}
      {placementPose ? (
        <InteractivePlacement pose={placementPose} gesturesEnabled={placementGesturesEnabled}>
          <group position={[0, props.mode === "webxr" || props.mode === "eighth-wall" ? props.arFloorOffsetY : 0, 0]}>
            <Suspense fallback={<LoadingGlyph />}>
              <props.Scene
                runtime={{ mode: props.mode, placed: props.placement !== null, placement: { position: placementPose.position, scale: placementPose.scale } }}
                quality={props.quality}
                reducedMotion={props.reducedMotion}
                resetEpoch={props.resetEpoch}
                mediaUrl={props.mediaUrl}
                posterUrl={props.posterUrl}
                controls={props.controls}
                onAction={props.onAction}
                setPlacementGesturesEnabled={setPlacementGesturesEnabled}
              />
            </Suspense>
          </group>
        </InteractivePlacement>
      ) : null}
      <PerformanceMonitor
        ms={750}
        iterations={3}
        threshold={0.65}
        bounds={() => [32, 48]}
        onDecline={() => {
          if (!isImmersiveMode(props.mode)) props.onQualityChange(props.quality === "high" ? "medium" : "low");
        }}
        onIncline={() => {
          if (!isImmersiveMode(props.mode)) props.onQualityChange(props.quality === "low" ? "medium" : "high");
        }}
      />
    </>
  );
}

function ExternalFrameOwner() {
  // A positive render priority lets XR8.Threejs own the render pass while
  // externally advanced R3F frames still run demo useFrame subscriptions.
  useFrame(() => undefined, 1);
  return null;
}

function InteractivePlacement({ pose, children, gesturesEnabled }: { pose: PlacementPose; children: ReactNode; gesturesEnabled: RefObject<boolean> }) {
  const group = usePlacementGestures(gesturesEnabled, pose);

  return (
    <group ref={group} position={pose.position} quaternion={pose.quaternion} scale={pose.scale}>
      {children}
    </group>
  );
}

function LoadingGlyph() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame((_, delta) => { if (ref.current) ref.current.rotation.z += delta * 1.5; });
  return (
    <mesh ref={ref}>
      <torusGeometry args={[0.25, 0.025, 12, 48]} />
      <meshBasicMaterial color="#58e9ff" />
    </mesh>
  );
}

function WebglLifecycle({ onLost }: { onLost?: () => void }) {
  const { gl } = useThree();

  useEffect(() => {
    if (!onLost) return undefined;
    return attachWebglLifecycle(gl.domElement, { onLost });
  }, [gl, onLost]);

  return null;
}
